/**
 * Iter117 — Auto-Cruise control center.
 *
 * Unified dashboard for the 3 free pipelines (Sunbiz, Multi-State,
 * No-Website). Operator toggles which sources are active, edits the
 * No-Website city rotation, and triggers a manual rotation.
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Play, Save, Plus, Trash2, Plane, Globe, Landmark, MapPin,
  ExternalLink, CheckCircle2, AlertCircle, Circle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoCruise() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/auto-cruise/config`),
        axios.get(`${API}/auto-cruise/runs`, { params: { limit: 10 } }),
      ]);
      setCfg(c.data); setRuns(r.data.runs || []);
    } catch (_e) { toast.error("Falha ao carregar Auto-Cruise"); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        no_website: {
          enabled: cfg.no_website.enabled,
          cities: cfg.no_website.cities,
          daily_limit: cfg.no_website.daily_limit,
        },
        sunbiz_enabled: cfg.sunbiz?.enabled ?? true,
        multi_state_enabled: cfg.multi_state?.enabled ?? true,
        ga_enabled: cfg.ga?.enabled ?? true,
      };
      await axios.put(`${API}/auto-cruise/config`, body);
      toast.success("Configuração salva");
      load();
    } catch (e) { toast.error("Falha ao salvar"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/auto-cruise/run-now`);
      toast.success("Rotação iniciada em background. Atualizando em 60s...");
      setTimeout(load, 60000);
    } catch (_e) { toast.error("Falha ao acionar"); }
    finally { setRunning(false); }
  };

  const setNw = (patch) =>
    setCfg({ ...cfg, no_website: { ...cfg.no_website, ...patch } });

  const addCity = () =>
    setNw({
      cities: [...cfg.no_website.cities,
               { name: "Nova Cidade", bbox: [0, 0, 0, 0] }],
    });

  const updateCity = (i, patch) => {
    const next = cfg.no_website.cities.slice();
    next[i] = { ...next[i], ...patch };
    setNw({ cities: next });
  };

  const removeCity = (i) =>
    setNw({ cities: cfg.no_website.cities.filter((_, k) => k !== i) });

  if (!cfg) {
    return (
      <div className="al-shell">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--al-text-muted)]" />
        </div>
      </div>
    );
  }

  const nw = cfg.no_website;
  const nextCity = nw.cities[(nw.rotation_index ?? 0) % nw.cities.length];

  return (
    <div className="al-shell" data-testid="auto-cruise-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Plane className="w-7 h-7 text-[#3B82F6]" />
              <h1 className="text-3xl font-bold text-[var(--al-text)]">
                Auto-Cruise
              </h1>
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                AUTO · $0
              </span>
            </div>
            <p className="text-[var(--al-text-muted)] text-sm mt-1">
              Pipeline diário automatizado. Próxima rotação No-Website:{" "}
              <strong className="text-[var(--al-text)]">{nextCity?.name}</strong> ·
              último run: {nw.last_run_at
                ? new Date(nw.last_run_at).toLocaleString("pt-BR")
                : "ainda não rodou"}
            </p>
            <p className="text-[12px] mt-1">
              <a
                href="/no-website-leads"
                data-testid="ac-link-nwl"
                className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline font-semibold"
              >
                Ver leads No-Website capturados <ExternalLink className="w-3 h-3" />
              </a>
              <span className="mx-2 text-[var(--al-text-muted)]">·</span>
              <a
                href="/sunbiz"
                data-testid="ac-link-sunbiz"
                className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline font-semibold"
              >
                Sunbiz <ExternalLink className="w-3 h-3" />
              </a>
              <span className="mx-2 text-[var(--al-text-muted)]">·</span>
              <a
                href="/multi-state"
                data-testid="ac-link-multistate"
                className="inline-flex items-center gap-1 text-[#3B82F6] hover:underline font-semibold"
              >
                Multi-State <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
          <div className="flex gap-2">
            <ClaireHelpButton testid="auto-cruise"
              question="Como funciona o Auto-Cruise — rotação de cidades, fontes ativas e execução automática?" />
            <button
              data-testid="ac-save-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[var(--al-row-alt)] hover:bg-[var(--al-border)] text-[var(--al-text)] text-[13px] font-semibold border border-[var(--al-border)]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar
            </button>
            <button
              data-testid="ac-run-now"
              onClick={runNow}
              disabled={running}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Executar agora
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
          <PipelineCard
            icon={Landmark}
            title="Sunbiz · FL LLCs"
            schedule="Diário 12:00 UTC"
            enabled={cfg.sunbiz?.enabled ?? true}
            onToggle={(v) => setCfg({ ...cfg, sunbiz: { enabled: v } })}
            testid="ac-toggle-sunbiz"
          />
          <PipelineCard
            icon={Landmark}
            title="Multi-State · NY + TX"
            schedule="Diário 11:00 UTC (Socrata)"
            enabled={cfg.multi_state?.enabled ?? true}
            onToggle={(v) => setCfg({ ...cfg, multi_state: { enabled: v } })}
            testid="ac-toggle-multistate"
          />
          <PipelineCard
            icon={Globe}
            title="No-Website · Overpass"
            schedule="Diário 10:30 UTC · rotação de cidades"
            enabled={nw.enabled}
            onToggle={(v) => setNw({ enabled: v })}
            testid="ac-toggle-nowebsite"
          />
          <PipelineCard
            icon={Landmark}
            title="Geórgia · SOS Registry"
            schedule="A cada rotação · keywords de indústria"
            enabled={cfg.ga?.enabled ?? true}
            onToggle={(v) => setCfg({ ...cfg, ga: { ...(cfg.ga || {}), enabled: v } })}
            testid="ac-toggle-ga"
          />
        </div>

        {/* No-Website city rotation editor */}
        <section className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[var(--al-text)] font-bold text-[14px]">
                Rotação de cidades · No-Website
              </div>
              <div className="text-[11.5px] text-[var(--al-text-muted)]">
                1 cidade por dia · {nw.cities.length} cidades = ciclo de {nw.cities.length} dias
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-[var(--al-text-muted)]">
                Leads/cidade:
              </label>
              <select
                data-testid="ac-daily-limit"
                value={nw.daily_limit}
                onChange={(e) => setNw({ daily_limit: Number(e.target.value) })}
                className="al-input h-8 text-[12px]"
              >
                {[50, 100, 200, 300].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                data-testid="ac-add-city"
                onClick={addCity}
                className="inline-flex items-center gap-1 h-8 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
              >
                <Plus className="w-3 h-3" /> Cidade
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {nw.cities.map((c, i) => {
              const isNext = i === (nw.rotation_index ?? 0) % nw.cities.length;
              const last = c.last_run;
              // Color signal per city: blue=next, green=ran ok, red=error, gray=untouched.
              const tone = isNext
                ? "border-[#3B82F6] bg-[#3B82F6]/5"
                : last?.ok
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : last?.error
                    ? "border-[#DC2626]/40 bg-[#DC2626]/5"
                    : "border-[var(--al-border)] bg-[var(--al-row)]";
              const StatusIcon = isNext
                ? Circle
                : last?.ok
                  ? CheckCircle2
                  : last?.error
                    ? AlertCircle
                    : Circle;
              const iconColor = isNext
                ? "text-[#3B82F6]"
                : last?.ok
                  ? "text-emerald-400"
                  : last?.error
                    ? "text-[#DC2626]"
                    : "text-[var(--al-text-muted)]";
              return (
                <div
                  key={i}
                  data-testid={`ac-city-${i}`}
                  className={`flex items-center gap-2 rounded-lg border ${tone} p-2.5 transition`}
                >
                  <StatusIcon className={`w-4 h-4 shrink-0 ${iconColor}`} />
                  {isNext && (
                    <span className="px-1.5 py-0.5 rounded bg-[#3B82F6] text-white text-[9px] font-bold uppercase">
                      PRÓXIMA
                    </span>
                  )}
                  {!isNext && last?.ok && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-bold uppercase">
                      OK · {last.inserted}
                    </span>
                  )}
                  {!isNext && last?.error && (
                    <span className="px-1.5 py-0.5 rounded bg-[#DC2626] text-white text-[9px] font-bold uppercase">
                      ERRO
                    </span>
                  )}
                  <MapPin className="w-3.5 h-3.5 text-[var(--al-text-muted)]" />
                  <input
                    value={c.name}
                    onChange={(e) => updateCity(i, { name: e.target.value })}
                    className="al-input h-8 text-[12.5px] w-40"
                  />
                  <span className="text-[10.5px] text-[var(--al-text-muted)] uppercase">
                    bbox:
                  </span>
                  {c.bbox.map((v, j) => (
                    <input
                      key={j}
                      type="number"
                      step="0.01"
                      value={v}
                      onChange={(e) => {
                        const next = c.bbox.slice();
                        next[j] = parseFloat(e.target.value) || 0;
                        updateCity(i, { bbox: next });
                      }}
                      className="al-input h-8 text-[11.5px] w-24 font-mono"
                      placeholder={["S", "W", "N", "E"][j]}
                    />
                  ))}
                  {last?.last_run_at && (
                    <span className="text-[10px] text-[var(--al-text-muted)]">
                      {new Date(last.last_run_at).toLocaleString("pt-BR")}
                    </span>
                  )}
                  <button
                    data-testid={`ac-remove-${i}`}
                    onClick={() => removeCity(i)}
                    className="ml-auto text-[var(--al-text-muted)] hover:text-[#DC2626]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
            {nw.cities.length === 0 && (
              <div className="text-[var(--al-text-muted)] text-[12px] italic py-3 text-center">
                Nenhuma cidade configurada — adicione pelo menos uma.
              </div>
            )}
          </div>
        </section>

        {/* Runs log */}
        <section className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
          <div className="text-[var(--al-text)] font-bold text-[14px] mb-3">
            Histórico de rotações
          </div>
          <div className="space-y-2">
            {runs.map((r, i) => (
              <div
                key={i}
                data-testid={`ac-run-${i}`}
                className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row)] p-2.5 text-[12px]"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10.5px] text-[var(--al-text-muted)]">
                    {new Date(r.started_at).toLocaleString("pt-BR")}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-[var(--al-border)] text-[10px] uppercase text-[var(--al-text-muted)]">
                    {r.triggered_by}
                  </span>
                  {r.no_website?.error ? (
                    <span className="text-[#DC2626] text-[11.5px]">
                      ❌ {r.no_website.city} · {r.no_website.error}
                    </span>
                  ) : (
                    <span className="text-[var(--al-text)] text-[11.5px]">
                      ✅ {r.no_website?.city} · {r.no_website?.inserted ?? 0} novos · {r.no_website?.skipped_already_captured ?? 0} skipped
                    </span>
                  )}
                </div>
              </div>
            ))}
            {!runs.length && (
              <div className="text-[var(--al-text-muted)] text-[12px] italic py-3 text-center">
                Nenhuma rotação ainda. Clique em "Executar agora".
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function PipelineCard({ icon: Icon, title, schedule, enabled, onToggle, testid }) {
  return (
    <div
      className={`rounded-xl border p-4 transition ${
        enabled
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-[var(--al-border)] bg-[var(--al-row-alt)]"
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${enabled ? "text-emerald-400" : "text-[var(--al-text-muted)]"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[var(--al-text)] text-[13.5px]">{title}</div>
          <div className="text-[10.5px] text-[var(--al-text-muted)] mt-0.5">{schedule}</div>
        </div>
        <button
          data-testid={testid}
          onClick={() => onToggle(!enabled)}
          className={`relative w-10 h-5 rounded-full transition ${
            enabled ? "bg-emerald-500" : "bg-[var(--al-border)]"
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${
              enabled ? "left-5" : "left-0.5"
            }`}
          />
        </button>
      </div>
    </div>
  );
}
